import { Box, useMantineTheme } from '@mantine/core';
import { forwardRef, useState, useEffect, useRef } from 'react';
import { useReducedMotion } from '../hooks';

export type SlideDirection = 'left' | 'right' | 'up' | 'down';

export interface PageSliderProps {
  children: React.ReactNode;
  pageKey: string | number;
  direction?: SlideDirection;
  duration?: number;
  timingFunction?: string;
}

/**
 * Slides pages in/out based on direction
 * Useful for wizards, onboarding flows, or tab content
 */
const PageSlider = forwardRef<HTMLDivElement, PageSliderProps>(
  (
    {
      children,
      pageKey,
      direction = 'left',
      duration = 300,
      timingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const [currentPage, setCurrentPage] = useState({ key: pageKey, content: children });
    const [nextPage, setNextPage] = useState<{ key: string | number; content: React.ReactNode } | null>(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const actualDuration = reducedMotion ? 0 : duration;

    useEffect(() => {
      if (pageKey !== currentPage.key && !isAnimating) {
        setNextPage({ key: pageKey, content: children });
        setIsAnimating(true);
      }
    }, [pageKey, children, currentPage.key, isAnimating]);

    useEffect(() => {
      if (isAnimating) {
        const timer = setTimeout(() => {
          if (nextPage) {
            setCurrentPage(nextPage);
            setNextPage(null);
          }
          setIsAnimating(false);
        }, actualDuration);

        return () => clearTimeout(timer);
      }
    }, [isAnimating, actualDuration, nextPage]);

    const getTransform = (isEntering: boolean) => {
      const distance = '100%';
      const exitDistance = isEntering ? '' : '-';
      const enterDistance = isEntering ? '-' : '';

      switch (direction) {
        case 'left':
          return isEntering
            ? `translateX(${enterDistance}${distance})`
            : `translateX(${exitDistance}${distance})`;
        case 'right':
          return isEntering
            ? `translateX(${exitDistance}${distance})`
            : `translateX(${enterDistance}${distance})`;
        case 'up':
          return isEntering
            ? `translateY(${enterDistance}${distance})`
            : `translateY(${exitDistance}${distance})`;
        case 'down':
          return isEntering
            ? `translateY(${exitDistance}${distance})`
            : `translateY(${enterDistance}${distance})`;
        default:
          return 'translateX(0)';
      }
    };

    return (
      <Box
        ref={ref}
        style={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          ref={containerRef}
          style={{
            transform: isAnimating ? getTransform(false) : 'translate(0)',
            transition: reducedMotion ? 'none' : `transform ${actualDuration}ms ${timingFunction}`,
          }}
        >
          {currentPage.content}
        </Box>

        {nextPage && isAnimating && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              transform: isAnimating ? 'translate(0)' : getTransform(true),
              transition: reducedMotion ? 'none' : `transform ${actualDuration}ms ${timingFunction}`,
            }}
          >
            {nextPage.content}
          </Box>
        )}
      </Box>
    );
  }
);

PageSlider.displayName = 'PageSlider';

export default PageSlider;
